import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, presentAnchors } from './tasks-help.shared';

/**
 * ?-Hilfe fuer "Meine Aufgaben" (`/tasks/my`).
 *
 * Die einzige Aufgaben-Tour aus der **Mitarbeiter**-Sicht — alle uebrigen
 * richten sich an Planende. Entsprechend traegt sie {@link Audience.Employee};
 * Teamleiter sind mitgemeint, weil sie dieselbe Seite fuer ihre eigenen
 * Aufgaben benutzen.
 *
 * Die eine Aussage, an der die Tour haengt: **hier steht nur, was DU zu tun
 * hast**, sortiert nach Dringlichkeit. Daraus folgen die drei haeufigen
 * Rueckfragen, die die Schritte 3 bis 5 beantworten:
 *
 *  1. "Wo ist meine Aufgabe von naechster Woche?" — die Gruppen heissen mit
 *     Auftraegen Als Naechstes / Wartet / Ausser Haus / Spaeter, ohne
 *     Diese Schicht / Ueberfaellig / Heute / Spaeter (EINE Spur nennt beide
 *     Saetze, Task 13); was weiter in der Zukunft liegt, erscheint erst zu
 *     seiner Startzeit.
 *  2. "Warum oeffnet sich beim Abhaken die Aufgabe?" — der Kreis hakt nur
 *     ohne Checkliste direkt ab; mit Checkliste oder fehlendem Nachweis
 *     oeffnet er die Aufgabe (Telefon: Sheet, ab 1024 px: Seitenflaeche).
 *  3. "Warum steht da eine Aufgabe, die nicht meine ist?" — eine Topf-Aufgabe
 *     gehoert noch niemandem, bis jemand sie uebernimmt.
 *
 * Belege im Code: `pages/my-tasks-page.component.ts` (Fokus-Karte, Akkordeon
 * der Gruppen, Abhak-Kreis, Topf-Karte, Oeffnen-Knopf) — dort sitzen auch alle
 * hier referenzierten Anker.
 *
 * Seiten-Tour, also KEIN `dialogContext`. Ausser der Seitenwurzel haengt jeder
 * Anker an Bestand (keine Fokus-Aufgabe, keine Zeile, kein Topf) — deshalb
 * sind die Schritte 2 bis 5 `optional` mit `skipIf` und die Zaehlung
 * umnummeriert.
 *
 * **Zwei Seiten, dieselben Anker (06.10.2026, Befund C-1).** Am Telefon steht
 * der Mitarbeiter im Tab „Aufgaben" (`pages/mobile/my-tasks-mobile.page.*`),
 * nicht auf `/tasks/my`. Die Mobilseite traegt seitdem denselben ?-Knopf und
 * dieselben Anker wie die Desktop-Seite. `readyAnchor` laesst die Tour dort
 * starten, wo die Seite schon steht (auch auf der Durchreiche
 * `/employee/dashboard/aufgaben`), `mobileRoute` fuehrt aus dem Hilfe-Hub in
 * den Tab statt auf die Desktop-Seite ohne Tab-Leiste.
 */

/**
 * Ringe der Station `my-groups`: die ersten beiden Gruppen, die es gibt.
 *
 * Mit „Auftraege nutzen" heissen die Gruppen „Als Naechstes · Wartet · Ausser Haus ·
 * Spaeter", ohne „Diese Schicht · Ueberfaellig · Heute · Spaeter". Bis 06.10.2026 zeigte
 * die Station fest auf `shift`/`today` — mit Auftraegen standen beide nie im DOM, und
 * jeder Zusatz-Anker kostete 2 s Wartezeit fuer einen Ring, der nie kam.
 */
export const MY_GROUPS_RING_CANDIDATES: readonly AnchorId[] = [
  AnchorId.MyTasksGroupShift,
  AnchorId.MyTasksGroupToday,
  AnchorId.MyTasksGroupNext,
  AnchorId.MyTasksGroupWaiting,
];
export const MY_TASKS_HELP_TOUR: TourConfig = {
  id: TourFeature.MyTasksHelp,
  version: 1,
  title: 'MY_TASKS_HELP_TOUR_TITLE',
  description: 'MY_TASKS_HELP_TOUR_DESC',
  audience: [Audience.Employee, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: {
    route: '/tasks/my',
    mobileRoute: '/employee-mobile/tabs/tab-tasks',
    readyAnchor: AnchorId.MyTasksPage,
    mode: 'auto',
  },
  triggerContext: {
    route: '/tasks/my',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 80,
  category: 'feature-discovery',
  // Vier von sechs Schritten haengen an Bestand. Ohne Umnummerierung zaehlte
  // das Popover auf einem leeren Konto "1 / 6 → 6 / 6".
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'MY_TASKS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'my-welcome',
          anchor: AnchorId.MyTasksPage,
          spotlightAnchor: AnchorId.MyTasksPage,
          title: 'TUTORIAL.MY_TASKS.WELCOME.TITLE',
          content: 'TUTORIAL.MY_TASKS.WELCOME.CONTENT',
          placement: 'bottom',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Fehlt, solange keine Aufgabe "jetzt dran" ist.
          id: 'my-focus',
          anchor: AnchorId.MyTasksFocus,
          spotlightAnchor: AnchorId.MyTasksFocus,
          title: 'TUTORIAL.MY_TASKS.FOCUS.TITLE',
          content: 'TUTORIAL.MY_TASKS.FOCUS.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.MyTasksFocus),
        },
        {
          // Kern-Step 1: die Gruppen. Die beiden Zusatz-Cutouts zeigen die
          // ersten beiden Gruppen, die es gibt ({@link MY_GROUPS_RING_CANDIDATES});
          // "Spaeter" steht darunter und braucht keinen eigenen Ring.
          id: 'my-groups',
          anchor: AnchorId.MyTasksGroups,
          spotlightAnchor: AnchorId.MyTasksGroups,
          get additionalSpotlightAnchors() {
            return presentAnchors(MY_GROUPS_RING_CANDIDATES, 2);
          },
          title: 'TUTORIAL.MY_TASKS.GROUPS.TITLE',
          content: 'TUTORIAL.MY_TASKS.GROUPS.CONTENT',
          audioStepId: 'my-groups-v2',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.MyTasksGroups),
        },
        {
          // Kern-Step 2: Abhak-Kreis vs. Erledigen-Fenster. Der Anker sitzt auf
          // der ERSTEN Zeile — ohne Aufgaben gibt es ihn nicht.
          id: 'my-row',
          anchor: AnchorId.MyTasksRow,
          spotlightAnchor: AnchorId.MyTasksRow,
          additionalSpotlightAnchors: [AnchorId.MyTasksCheckCircle],
          title: 'TUTORIAL.MY_TASKS.ROW.TITLE',
          content: 'TUTORIAL.MY_TASKS.ROW.CONTENT',
          audioStepId: 'my-row-v2',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.MyTasksRow),
        },
        {
          // Kern-Step 3: Topf-Aufgabe. Nur im DOM, wenn ueberhaupt eine
          // uebernehmbare Aufgabe ansteht.
          id: 'my-pool',
          anchor: AnchorId.MyTasksPool,
          spotlightAnchor: AnchorId.MyTasksPool,
          title: 'TUTORIAL.MY_TASKS.POOL.TITLE',
          content: 'TUTORIAL.MY_TASKS.POOL.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.MyTasksPool),
        },
        {
          // Ueberleitung ins Erledigen-Fenster: die Folge-Tour oeffnet es
          // selbst ueber `MyTasksRowOpen`.
          id: 'my-next',
          anchor: AnchorId.MyTasksPage,
          spotlightAnchor: AnchorId.MyTasksPage,
          title: 'TUTORIAL.MY_TASKS.NEXT.TITLE',
          content: 'TUTORIAL.MY_TASKS.NEXT.CONTENT',
          audioStepId: 'my-next-v2',
          placement: 'bottom',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          actions: [
            {
              labelKey: 'TUTORIAL.MY_TASKS.NEXT.ACTION',
              icon: 'fa-light fa-circle-check',
              startTour: TourFeature.TaskCompleteSheetHelp,
              variant: 'primary',
            },
          ],
        },
      ],
    },
  ],
};
